import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "../api/client";

export interface AgentCapability {
  id: string;
  namespace: string;
  description: string;
  taxonomy_level: number;
}

export interface AvailableAgent {
  id: string;
  name: string;
  surface: string;
  is_xamun_default: boolean;
  auth_method: string;
  description: string;
  capabilities: AgentCapability[];
  is_bound_in_workspace: boolean;
}

export interface AgentBinding {
  id: string;
  agent_definition_id: string;
  agent_name: string;
  surface: string;
  enabled: boolean;
  scope: Record<string, unknown>;
  bound_by_user: { id: string; email: string; display_name: string };
  bound_at: string;
  expires_at: string | null;
  last_health_check_at: string | null;
  last_invocation_at: string | null;
  telemetry: Record<string, unknown>;
}

export interface CoverageRow {
  id: string;
  namespace: string;
  description: string;
  taxonomy_level: number;
  covered_by: Array<{ binding_id: string; agent_name: string }>;
}

export interface CoverageMapResponse {
  capabilities: CoverageRow[];
  summary: { total: number; covered: number; gaps: number };
}

/**
 * Company-scoped LLM config as returned by /api/workspaces/:wid/llm-configs.
 * One row per provider; exactly one row has isDefault=true once the company
 * has any configured provider.
 */
export interface CompanyLlmConfig {
  provider: "anthropic" | "openai" | "gemini" | "azure_openai";
  config: { model?: string;[k: string]: unknown };
  routingRules: Record<string, unknown>;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useAvailableAgents(workspaceId: string) {
  return useQuery<{ agents: AvailableAgent[] }, Error>({
    queryKey: ["agents", workspaceId, "available"],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ agents: AvailableAgent[] }>(
        `/api/workspaces/${workspaceId}/agents/available`,
      ),
  });
}

export function useBindings(workspaceId: string) {
  return useQuery<{ bindings: AgentBinding[] }, Error>({
    queryKey: ["agents", workspaceId, "bindings"],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ bindings: AgentBinding[] }>(
        `/api/workspaces/${workspaceId}/agents/bindings`,
      ),
  });
}

export function useCoverageMap(workspaceId: string) {
  return useQuery<CoverageMapResponse, Error>({
    queryKey: ["agents", workspaceId, "coverage-map"],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<CoverageMapResponse>(
        `/api/workspaces/${workspaceId}/agents/coverage-map`,
      ),
  });
}

export interface BindAgentBody {
  agent_definition_name: string;
  scope?: Record<string, unknown>;
  credentials: Record<string, unknown>;
}

export interface BindAgentResponse {
  id?: string;
  agent_definition_id?: string;
  agent_name?: string;
  surface?: string;
  enabled?: boolean;
  scope?: Record<string, unknown>;
  bound_at?: string;
  // OAuth-redirect variant for copilot
  next_step?: "oauth_redirect";
  authorize_url?: string;
  state?: string;
}

export function useAgentMutations(workspaceId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["agents", workspaceId] });
  };

  const bind = useMutation<BindAgentResponse, Error, BindAgentBody>({
    mutationFn: (body) =>
      apiPost<BindAgentResponse>(
        `/api/workspaces/${workspaceId}/agents/bindings`,
        body,
      ),
    onSuccess: invalidate,
  });

  const revoke = useMutation<unknown, Error, string>({
    mutationFn: (bindingId) =>
      apiDelete(
        `/api/workspaces/${workspaceId}/agents/bindings/${bindingId}`,
      ),
    onSuccess: invalidate,
  });

  const testConnection = useMutation<
    { ok: boolean; error?: string },
    Error,
    string
  >({
    mutationFn: (bindingId) =>
      apiPost<{ ok: boolean; error?: string }>(
        `/api/workspaces/${workspaceId}/agents/bindings/${bindingId}/test-connection`,
        {},
      ),
  });

  return { bind, revoke, testConnection };
}

/**
 * Lists every LLM config configured for the workspace's company. Returns
 * `{items: [...]}`; empty array = no key configured (the gating signal for
 * agent runs). The component-friendly `hasDefault` derives the inactive
 * state shown across HandsBuilder + AgentManagement.
 */
export function useCompanyLlmConfigs(workspaceId: string) {
  return useQuery<{ items: CompanyLlmConfig[] }, Error>({
    queryKey: ["llm-configs", workspaceId],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ items: CompanyLlmConfig[] }>(
        `/api/workspaces/${workspaceId}/llm-configs`,
      ),
  });
}

/** Convenience: did this workspace's company configure any LLM key? */
export function useHasLlmKey(workspaceId: string): {
  isLoading: boolean;
  hasDefault: boolean;
  defaultProvider: CompanyLlmConfig["provider"] | null;
} {
  const q = useCompanyLlmConfigs(workspaceId);
  const items = q.data?.items ?? [];
  const def = items.find((c) => c.isDefault) ?? null;
  return {
    isLoading: q.isLoading,
    hasDefault: !!def,
    defaultProvider: def?.provider ?? null,
  };
}

export interface SetCompanyLlmConfigBody {
  credentials: Record<string, unknown>;
  config?: Record<string, unknown>;
  routing_rules?: Record<string, unknown>;
  is_default?: boolean;
}

export function useCompanyLlmMutations(workspaceId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["llm-configs", workspaceId] });

  const set = useMutation<
    unknown,
    Error,
    { provider: CompanyLlmConfig["provider"]; body: SetCompanyLlmConfigBody }
  >({
    mutationFn: ({ provider, body }) =>
      apiPut(
        `/api/workspaces/${workspaceId}/llm-configs/${provider}`,
        body,
      ),
    onSuccess: invalidate,
  });

  const promoteDefault = useMutation<
    unknown,
    Error,
    CompanyLlmConfig["provider"]
  >({
    mutationFn: (provider) =>
      apiPost(
        `/api/workspaces/${workspaceId}/llm-configs/${provider}/default`,
        {},
      ),
    onSuccess: invalidate,
  });

  const test = useMutation<
    { ok: boolean; detail?: string },
    Error,
    CompanyLlmConfig["provider"]
  >({
    mutationFn: (provider) =>
      apiPost<{ ok: boolean; detail?: string }>(
        `/api/workspaces/${workspaceId}/llm-configs/${provider}/test`,
        {},
      ),
  });

  const remove = useMutation<unknown, Error, CompanyLlmConfig["provider"]>({
    mutationFn: (provider) =>
      apiDelete(`/api/workspaces/${workspaceId}/llm-configs/${provider}`),
    onSuccess: invalidate,
  });

  return { set, promoteDefault, test, remove };
}

